import { firebaseAuthHeaders, hostingFunctionPath } from '../firebase/functionsUrl';

export type FunctionAuth =
  | 'public'
  | 'current-user'
  | { idToken: string };

export type FunctionsTransportErrorKind =
  | 'authentication'
  | 'http'
  | 'invalid-response'
  | 'network';

export class FunctionsTransportError extends Error {
  readonly kind: FunctionsTransportErrorKind;
  readonly status?: number;
  readonly responseBody?: unknown;

  constructor(
    kind: FunctionsTransportErrorKind,
    message: string,
    options: {
      status?: number;
      responseBody?: unknown;
      cause?: unknown;
    } = {},
  ) {
    super(message);
    this.name = 'FunctionsTransportError';
    this.kind = kind;
    this.status = options.status;
    this.responseBody = options.responseBody;
    if (options.cause !== undefined) {
      (this as Error & { cause?: unknown }).cause = options.cause;
    }
  }
}

export interface FunctionRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  auth?: FunctionAuth;
  body?: unknown;
  signal?: AbortSignal;
  /** Additional HTTP statuses accepted by an endpoint-specific contract. */
  acceptedStatuses?: readonly number[];
}

function errorMessageFrom(body: unknown): string | undefined {
  if (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof body.error === 'string' &&
    body.error.trim() !== ''
  ) {
    return body.error;
  }
  return undefined;
}

async function headersFor(authMode: FunctionAuth): Promise<Record<string, string>> {
  if (authMode === 'public') {
    return { 'Content-Type': 'application/json' };
  }

  if (authMode === 'current-user') {
    // Public and explicit-token calls must not initialize the Firebase SDK just
    // by importing the transport. Load Auth only for the mode that needs it.
    let auth: (typeof import('../firebase/config'))['auth'];
    try {
      ({ auth } = await import('../firebase/config'));
    } catch (cause) {
      throw new FunctionsTransportError(
        'authentication',
        'Unable to initialize Firebase authentication',
        { cause },
      );
    }
    const user = auth.currentUser;
    if (!user) {
      throw new FunctionsTransportError(
        'authentication',
        'Admin authentication is required',
      );
    }
    try {
      return firebaseAuthHeaders(await user.getIdToken());
    } catch (cause) {
      throw new FunctionsTransportError(
        'authentication',
        'Unable to authenticate the current user',
        { cause },
      );
    }
  }

  return firebaseAuthHeaders(authMode.idToken);
}

function normalizeFunctionPath(path: string): string {
  return path.replace(/^\/+/, '').replace(/^api(?:\/|$)/, '');
}

/**
 * Calls an HTTP Cloud Function through the same-origin Firebase Hosting rewrite.
 * Authentication is carried in X-Firebase-Auth because Hosting strips the
 * Authorization header before forwarding onRequest calls.
 */
export async function requestFunctionJson<T>(
  path: string,
  options: FunctionRequestOptions = {},
): Promise<T> {
  const {
    method = 'GET',
    auth: authMode = 'public',
    body,
    signal,
    acceptedStatuses = [],
  } = options;
  const headers = await headersFor(authMode);

  let response: Response;
  try {
    response = await fetch(hostingFunctionPath(normalizeFunctionPath(path)), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (cause) {
    throw new FunctionsTransportError(
      'network',
      'Unable to reach the server',
      { cause },
    );
  }

  let responseBody: unknown;
  const statusAccepted = response.ok || acceptedStatuses.includes(response.status);
  try {
    responseBody = await response.json();
  } catch (cause) {
    if (!statusAccepted) {
      throw new FunctionsTransportError(
        'http',
        `Function request failed (${response.status})`,
        { status: response.status, cause },
      );
    }
    throw new FunctionsTransportError(
      'invalid-response',
      `Function returned an invalid JSON response (${response.status})`,
      { status: response.status, cause },
    );
  }

  if (!statusAccepted) {
    throw new FunctionsTransportError(
      'http',
      errorMessageFrom(responseBody) ?? `Function request failed (${response.status})`,
      { status: response.status, responseBody },
    );
  }

  return responseBody as T;
}
